/**
 * Rich fictional ledger content for demos — polished copy, no “sample/demo” disclaimers in titles.
 */

import {
  DEMO_INDIVIDUAL_DISPLAY_NAME,
  DEMO_INSTITUTION_DISPLAY_NAME,
  isDemoIndividualUser,
  isDemoInstitutionUser,
} from '@/lib/demo-content';
import { isPresentationDemoUserId } from '@/lib/presentation-demo';

/** Institution dashboard + inbox: use rich mocks when Supabase rows are empty. */
export function institutionUsesRichDemo(identifier: string | undefined | null): boolean {
  return isPresentationDemoUserId(identifier) || isDemoInstitutionUser(identifier);
}

export function individualUsesRichDemo(identifier: string | undefined | null): boolean {
  return isPresentationDemoUserId(identifier) || isDemoIndividualUser(identifier);
}

/** Milestone badges for Overview when `user_badges` is empty (demo / presentation). */
export function demoEarnedUserBadgeKeys(): string[] {
  const achs = demoAchievementsStoryHubShape();
  const pillars = new Set(achs.map((a) => a.pillar));
  const keys = ['first_record', 'verified', 'storyteller'];
  if (pillars.size >= 4) keys.push('well_rounded');
  return keys;
}

/** Pillar depth badges for Overview when `pillar_badges` is empty (demo / presentation). */
export function demoPillarBadgesForOverview(): Array<{ pillar: string; awarded_at: string }> {
  const counts = new Map<string, number>();
  for (const a of demoAchievementsStoryHubShape()) {
    counts.set(a.pillar, (counts.get(a.pillar) || 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([pillar], i) => ({
      pillar,
      awarded_at: new Date(Date.now() - (45 + i * 12) * 86400000).toISOString(),
    }));
}

/** Rows for `AchievementsTable` when Supabase returns none (demo / presentation). */
export function demoIndividualAchievementsTableRows(): Array<{
  id: string;
  achievement_ref: string;
  title: string;
  pillar: string;
  institution: string;
  submitted_date: string;
  status: 'verified' | 'pending' | 'rejected' | 'under_review';
  description: string;
  verification_ref?: string;
  verified_at?: string;
}> {
  return demoAchievementsStoryHubShape().map((a) => ({
    id: a.id,
    achievement_ref: a.achievement_ref,
    title: a.title,
    pillar: a.pillar,
    institution: a.institution,
    submitted_date: a.submitted_date,
    status: a.status as 'verified' | 'pending' | 'rejected' | 'under_review',
    description: a.description,
    verification_ref: a.verification_ref,
    verified_at: a.verified_at,
  }));
}

export const U = (id: string, w = 1200) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=80`;

const daysAgo = (n: number) => new Date(Date.now() - n * 86400000).toISOString();
const isoDate = (n: number) => daysAgo(n).slice(0, 10);

const VERIFYERS = [
  'Pacific Heritage Museum',
  'Coastal Arts Conservatory',
  'North Shore Public Library',
  'Riverbend Film Guild',
  'Metro Civic Awards Council',
  'Straitview University Continuing Ed',
  'Harbourfront Lifesaving Association',
  'Pacific Northwest Literary Review',
  'InnovateBC Small Grants Program',
  'General Hospital Clinical Education',
  'Open Heritage Localization Collective',
  'Coastal Marathon Charities',
  'Trust & Safety Hackathon Collective',
  'Pacific Tea Culture Institute',
  'Regional Youth Jazz Alliance',
] as const;

/** Story Hub + credential-shaped achievements (15) */
export function demoAchievementsStoryHubShape(): Array<{
  id: string;
  achievement_ref: string;
  title: string;
  pillar: string;
  institution: string;
  submitted_date: string;
  achievement_date: string | null;
  status: string;
  description: string;
  verification_type?: string;
  impact_moment: string | null;
  verification_ref?: string;
  verified_at?: string;
  verified_by?: string;
}> {
  const rows: Array<{
    title: string;
    pillar: string;
    instIdx: number;
    status: 'verified' | 'pending' | 'under_review';
    desc: string;
    impact: string | null;
    achDate: string | null;
    submittedOffset: number;
  }> = [
    {
      title: 'Cultural Bridge Fellowship — Immersion with Distinction',
      pillar: 'Achievement',
      instIdx: 0,
      status: 'verified',
      desc: 'Completed an intensive English immersion program with portfolio review, public narrative presentation, and peer mentorship hours documented by the museum’s education partner.',
      impact:
        'Delivered closing address to incoming cohort; two instructors signed witness statements.',
      achDate: '2024-06-12',
      submittedOffset: 20,
    },
    {
      title: 'Youth Mentorship Cohort Lead — 48 Documented Hours',
      pillar: 'Hero',
      instIdx: 1,
      status: 'verified',
      desc: 'Led a semester-long mentorship circle for new students: goal-setting workshops, résumé clinics, and weekend study halls.',
      impact:
        'Twelve mentees completed the term with improved attendance; program renewed for next year.',
      achDate: '2024-09-01',
      submittedOffset: 45,
    },
    {
      title: 'Winter Response Community Citation',
      pillar: 'Hero',
      instIdx: 4,
      status: 'verified',
      desc: 'Recognized for coordinated snow-response support: walkway clearing, wellness checks, and supply runs during a regional ice event.',
      impact: 'Neighbourhood association cited reliability and calm communication under pressure.',
      achDate: '2023-12-20',
      submittedOffset: 400,
    },
    {
      title: 'Digital Heritage Scanner Operator Certification',
      pillar: 'Performance',
      instIdx: 0,
      status: 'verified',
      desc: 'Training in fragile document handling, colour-calibrated capture, and metadata workflows for archive intake.',
      impact: 'Qualified to operate floor scanners for the museum’s public digitization weekends.',
      achDate: '2025-01-08',
      submittedOffset: 12,
    },
    {
      title: 'Coast-to-Coast Policy Debate — Semifinalist',
      pillar: 'Performance',
      instIdx: 5,
      status: 'verified',
      desc: 'Competed in bilingual policy debate series; briefs covered immigration integration and municipal housing.',
      impact:
        'Team ranked in top eight; individual speaker award for clarity under cross-examination.',
      achDate: '2024-03-18',
      submittedOffset: 300,
    },
    {
      title: 'Riverbend Festival — Official Short Film Selection',
      pillar: 'Favorites / Products',
      instIdx: 3,
      status: 'verified',
      desc: 'Twelve-minute narrative short selected for regional showcase; credited as director and editor.',
      impact: 'Screened to three sold-out houses; audience choice runner-up.',
      achDate: '2024-08-04',
      submittedOffset: 220,
    },
    {
      title: 'Bronze Medallion — Open Water Lifesaving',
      pillar: 'Achievement',
      instIdx: 6,
      status: 'verified',
      desc: 'Earned open-water rescue certification including timed swims, tow drills, and scenario-based first response.',
      impact: 'Eligible for beach volunteer roster next season.',
      achDate: '2025-05-02',
      submittedOffset: 8,
    },
    {
      title: 'Published Essay — “Carry-ons and Courage”',
      pillar: 'History',
      instIdx: 7,
      status: 'verified',
      desc: 'Creative nonfiction piece on migration and memory published in the review’s spring issue.',
      impact: 'Work taught in two community-college reading circles as supplementary text.',
      achDate: '2024-11-15',
      submittedOffset: 90,
    },
    {
      title: 'Neighbourhood Pop-Up Grant — Micro-Retail Pilot',
      pillar: 'News',
      instIdx: 8,
      status: 'verified',
      desc: 'Awarded seed funding for a weekend artisan market stall featuring hand-bound journals and letterpress cards.',
      impact: 'Sold through first print run; vendor network invited back for winter night market.',
      achDate: '2024-07-22',
      submittedOffset: 180,
    },
    {
      title: 'Clinical Foundations Rotation — Excellence Note',
      pillar: 'Achievement',
      instIdx: 9,
      status: 'verified',
      desc: 'Pre-health pathway rotation with documented strengths in patient rapport, chart accuracy, and team handoffs.',
      impact: 'Preceptor recommendation filed for advanced placement elective.',
      achDate: '2025-02-14',
      submittedOffset: 5,
    },
    {
      title: 'Open-Source UI Localization — 2,100+ Strings',
      pillar: 'Grit / Mischief',
      instIdx: 10,
      status: 'pending',
      desc: 'Volunteer localization for a civic-transit accessibility app; Traditional Chinese and English variants.',
      impact: null,
      achDate: null,
      submittedOffset: 3,
    },
    {
      title: 'Community Food Security Leadership Award',
      pillar: 'Hero',
      instIdx: 4,
      status: 'pending',
      desc: 'Nominated for organizing a bi-weekly community meal program serving 80+ households; coordinated ten volunteer shifts over six months.',
      impact: null,
      achDate: null,
      submittedOffset: 6,
    },
    {
      title: 'Short Film Score — Regional Arts Commission',
      pillar: 'Favorites / Products',
      instIdx: 3,
      status: 'pending',
      desc: 'Original composition for a 22-minute documentary on coastal Indigenous land acknowledgment; submitted to regional arts grant.',
      impact: null,
      achDate: null,
      submittedOffset: 9,
    },
    {
      title: 'Emergency Response Volunteer — First Aid Level 2',
      pillar: 'Hero',
      instIdx: 6,
      status: 'under_review',
      desc: 'Completed 40-hour field training with Harbourfront Lifesaving: cardiac response, triage staging, and crowd-management protocols.',
      impact: null,
      achDate: null,
      submittedOffset: 14,
    },
    {
      title: 'Graduate Certificate Application — Heritage Archival Practice',
      pillar: 'Achievement',
      instIdx: 5,
      status: 'under_review',
      desc: 'Application submitted for part-time graduate certificate; supporting portfolio includes three practicum assessments and two letters from the museum education team.',
      impact: null,
      achDate: null,
      submittedOffset: 18,
    },
    {
      title: 'Harbour Relay — Charity Team Finisher',
      pillar: 'Performance',
      instIdx: 11,
      status: 'verified',
      desc: 'Leg-three anchor for mixed relay; fundraising target exceeded for literacy nonprofit.',
      impact: 'Team raised 118% of goal; split time within cohort top quartile.',
      achDate: '2024-10-06',
      submittedOffset: 150,
    },
    {
      title: 'Trust & Safety Hackathon — Integrity Track Winner',
      pillar: 'News',
      instIdx: 12,
      status: 'verified',
      desc: 'Built prototype ledger hooks for tamper-evident credential sharing with privacy-preserving previews.',
      impact: 'Judges cited clear threat modeling and humane copy for end-user consent.',
      achDate: '2025-03-30',
      submittedOffset: 2,
    },
    {
      title: 'Tea Ceremony — Student Demonstration Credential',
      pillar: 'History',
      instIdx: 13,
      status: 'under_review',
      desc: 'Documented preparation, posture, and seasonal utensil selection for winter gathering format.',
      impact: null,
      achDate: null,
      submittedOffset: 1,
    },
    {
      title: 'Regional Youth Jazz — Lead Alto Showcase',
      pillar: 'Performance',
      instIdx: 14,
      status: 'verified',
      desc: 'Featured soloist on three charts; ensemble received adjudicator commendation for intonation and swing feel.',
      impact: 'Invitation to summer conservatory intensive.',
      achDate: '2024-04-28',
      submittedOffset: 260,
    },
  ];

  return rows.map((r, i) => {
    const n = i + 1;
    const ref = `ERT-2026-${String(n).padStart(4, '0')}`;
    const institution = VERIFYERS[r.instIdx] ?? VERIFYERS[0];
    const submitted = isoDate(r.submittedOffset);
    const verified = r.status === 'verified';
    return {
      id: `ledger-ach-${n}`,
      achievement_ref: ref,
      title: r.title,
      pillar: r.pillar,
      institution,
      submitted_date: submitted,
      achievement_date: r.achDate,
      status: r.status,
      description: r.desc,
      verification_type: 'institution',
      impact_moment: r.impact,
      verification_ref: verified ? `VRF-${ref}` : undefined,
      verified_at: verified ? daysAgo(r.submittedOffset - 2) : undefined,
      verified_by: verified ? `${institution} · Records desk` : undefined,
    };
  });
}

export type DemoCommunityStory = {
  id: string;
  user_id: string;
  author_name: string;
  author_avatar_url?: string;
  type: 'Individual' | 'Institution';
  time: string;
  pillar_tag: string;
  title: string;
  story: string;
  witness_count: number;
  comments_count: number;
  endorsements: number;
  is_verified: boolean;
  is_institution: boolean;
  created_at: string;
};

const UNS = (id: string) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=120&q=80`;

const AUTHOR_AVATARS: Record<string, string> = {
  [DEMO_INDIVIDUAL_DISPLAY_NAME]: UNS('photo-1573497019940-1c28c88b4f3e'),
  'Rowan Okonkwo': UNS('photo-1507003211169-0a1dd7228f2d'),
  'Sienna Park': UNS('photo-1438761681033-6461ffad8d80'),
  'Mateo Reyes': UNS('photo-1472099645785-5658abf4ff4e'),
  'Amara Singh': UNS('photo-1544005313-94ddf0286df2'),
  'Leo Nakamura': UNS('photo-1506794778202-cad84cf45f1d'),
  'Dr. Helen Frost': UNS('photo-1573496359142-b8d87734a5a2'),
  'Jordan Malik': UNS('photo-1519345182560-3f2917c472ef'),
};

const NAMES = [
  DEMO_INDIVIDUAL_DISPLAY_NAME,
  'Rowan Okonkwo',
  'Sienna Park',
  'Mateo Reyes',
  'Amara Singh',
  'Leo Nakamura',
  'Dr. Helen Frost',
  'Jordan Malik',
  'Elena Vasquez',
  'Chris Oduya',
  DEMO_INSTITUTION_DISPLAY_NAME,
  'Harbour City Archives',
  'Straitview Robotics Club',
  'North Pacific Maritime Museum',
  'Coastal Youth Orchestra',
];

export function demoCommunityExplorerStories(): DemoCommunityStory[] {
  const t = (i: number) => `${i + 1}d ago`;
  const items: Omit<DemoCommunityStory, 'id' | 'user_id'>[] = [
    {
      author_name: DEMO_INDIVIDUAL_DISPLAY_NAME,
      type: 'Individual',
      time: t(2),
      pillar_tag: 'Hero',
      title: 'Ice storm week: thermoses, shovels, and quiet heroism',
      story:
        'Power flickered twice. We cleared two stoops, traded soup recipes, and someone brought extra salt for the ramp. Nothing heroic on a billboard—just neighbours choosing care.',
      witness_count: 34,
      comments_count: 9,
      endorsements: 22,
      is_verified: false,
      is_institution: false,
      created_at: daysAgo(2),
    },
    {
      author_name: DEMO_INDIVIDUAL_DISPLAY_NAME,
      type: 'Individual',
      time: t(4),
      pillar_tag: 'Performance',
      title: 'Three minutes on stage that rewired my nerves',
      story:
        'Welcome week mic, hands shaking, friends in row four. I spoke about learning a city by its bus routes. Applause was gentle; the relief was loud inside.',
      witness_count: 28,
      comments_count: 6,
      endorsements: 19,
      is_verified: false,
      is_institution: false,
      created_at: daysAgo(4),
    },
    {
      author_name: DEMO_INSTITUTION_DISPLAY_NAME,
      type: 'Institution',
      time: '1w ago',
      pillar_tag: 'History',
      title: 'Listening booth preview: newcomer voices of the Pacific Northwest',
      story:
        'Curators paired anonymized clips with stills from donated family albums. Consent trails are tight; every clip carries a rights tier before it can ship to the public heritage page.',
      witness_count: 41,
      comments_count: 11,
      endorsements: 27,
      is_verified: true,
      is_institution: true,
      created_at: daysAgo(7),
    },
    {
      author_name: DEMO_INSTITUTION_DISPLAY_NAME,
      type: 'Institution',
      time: '1w ago',
      pillar_tag: 'News',
      title: 'Elders’ circle: tea, interpreters, and archived warmth',
      story:
        'Monthly rhythm: arrive early, set low lights, run line checks. Interpreters trade headsets at break; students practice gentle follow-up questions. Audio masters stay in cold storage until metadata passes two reviews.',
      witness_count: 36,
      comments_count: 8,
      endorsements: 21,
      is_verified: true,
      is_institution: true,
      created_at: daysAgo(8),
    },
    {
      author_name: 'Rowan Okonkwo',
      type: 'Individual',
      time: t(3),
      pillar_tag: 'Achievement',
      title: 'From mock trials to real court observation hours',
      story:
        'Our team lost the final round but won something quieter: a judge lingered after to talk about diction under pressure. I logged every hour in the ledger—proof I showed up scared and stayed.',
      witness_count: 19,
      comments_count: 4,
      endorsements: 14,
      is_verified: false,
      is_institution: false,
      created_at: daysAgo(3),
    },
    {
      author_name: 'Sienna Park',
      type: 'Individual',
      time: t(5),
      pillar_tag: 'Grit / Mischief',
      title: 'The winter beater taught me maintenance is dignity',
      story:
        'Rust spots, honest tires, a heater that hums like a promise. I learned to read a dipstick and a friendship—my cousin on video call walked me through the first oil change.',
      witness_count: 52,
      comments_count: 14,
      endorsements: 31,
      is_verified: false,
      is_institution: false,
      created_at: daysAgo(5),
    },
    {
      author_name: 'Mateo Reyes',
      type: 'Individual',
      time: t(6),
      pillar_tag: 'Favorites / Products',
      title: 'A pop-up zine table became a small studio',
      story:
        'Risograph ink on cheap paper—limited runs, hand-numbered. Customers talked about grief and gardens. I never expected profit; I got correspondence from three cities.',
      witness_count: 24,
      comments_count: 7,
      endorsements: 18,
      is_verified: false,
      is_institution: false,
      created_at: daysAgo(6),
    },
    {
      author_name: 'Harbour City Archives',
      type: 'Institution',
      time: '2w ago',
      pillar_tag: 'History',
      title: 'Shipwright ledgers digitized — rope tension in marginalia',
      story:
        'Volunteers transcribed pencil notes beside cargo tallies. A maritime historian flagged a pattern in repair cycles; we linked it to weather tables from the same decade.',
      witness_count: 29,
      comments_count: 5,
      endorsements: 16,
      is_verified: true,
      is_institution: true,
      created_at: daysAgo(14),
    },
    {
      author_name: 'Amara Singh',
      type: 'Individual',
      time: t(9),
      pillar_tag: 'Performance',
      title: 'Solo tabla for a fundraiser that almost didn’t happen',
      story:
        'Venue flooded; we moved to a library atrium. Acoustics were wild—I leaned into softer bols and let the room breathe. Donations still cleared the goal.',
      witness_count: 44,
      comments_count: 10,
      endorsements: 26,
      is_verified: false,
      is_institution: false,
      created_at: daysAgo(9),
    },
    {
      author_name: 'Leo Nakamura',
      type: 'Individual',
      time: t(10),
      pillar_tag: 'News',
      title: 'Civic design jam: bus stops, lighting, and late shifts',
      story:
        'We mapped where workers wait past sunset. The city planner asked for one slide with witness quotes. I pulled lines from night-shift nurses and theatre ushers—voices that rarely get CAD layers.',
      witness_count: 31,
      comments_count: 8,
      endorsements: 20,
      is_verified: false,
      is_institution: false,
      created_at: daysAgo(10),
    },
    {
      author_name: 'Straitview Robotics Club',
      type: 'Institution',
      time: '2w ago',
      pillar_tag: 'Achievement',
      title: 'FIRST league — gracious professionalism under neon gym lights',
      story:
        'Drive team swapped joysticks mid-match; pit crew kept coolant jokes flowing. Judges noted our repair log: timestamped, photographed, boring in the best way.',
      witness_count: 67,
      comments_count: 15,
      endorsements: 40,
      is_verified: true,
      is_institution: true,
      created_at: daysAgo(15),
    },
    {
      author_name: 'Dr. Helen Frost',
      type: 'Individual',
      time: t(11),
      pillar_tag: 'Hero',
      title: 'Rural clinic Saturdays: intake triage in two languages',
      story:
        'We ran out of pediatric stickers; kids accepted crayon stars instead. A grandmother insisted I take tea breaks—“your hands shake when you skip food.” She was right.',
      witness_count: 22,
      comments_count: 6,
      endorsements: 17,
      is_verified: false,
      is_institution: false,
      created_at: daysAgo(11),
    },
    {
      author_name: 'North Pacific Maritime Museum',
      type: 'Institution',
      time: '3w ago',
      pillar_tag: 'Performance',
      title: 'Lighthouse lens cleaning livestream — 14k tuned in',
      story:
        'Conservation filmed every quarter-turn. Chat asked surprisingly good questions about UV filters; interns answered with citations. Merch sales funded a humidity sensor upgrade.',
      witness_count: 58,
      comments_count: 19,
      endorsements: 33,
      is_verified: true,
      is_institution: true,
      created_at: daysAgo(21),
    },
    {
      author_name: 'Jordan Malik',
      type: 'Individual',
      time: t(12),
      pillar_tag: 'History',
      title: 'Grandmother’s recipe cards, retyped and rescanned',
      story:
        'Oil stains became metadata fields: “oven hot,” “pinch means pinch.” Cousins argued over cardamom; we recorded both versions. The ledger holds the feud kindly.',
      witness_count: 17,
      comments_count: 5,
      endorsements: 12,
      is_verified: false,
      is_institution: false,
      created_at: daysAgo(12),
    },
    {
      author_name: 'Coastal Youth Orchestra',
      type: 'Institution',
      time: '3w ago',
      pillar_tag: 'Favorites / Products',
      title: 'Side-by-side concert: pros and teens share a stage',
      story:
        'Program notes included student essays on practice room grit. Ticket revenue seeded instrument repair scholarships. Encores were messy and sincere.',
      witness_count: 39,
      comments_count: 9,
      endorsements: 24,
      is_verified: true,
      is_institution: true,
      created_at: daysAgo(22),
    },
  ];

  return items.map((row, i) => ({
    ...row,
    id: `comm-story-${i + 1}`,
    user_id: `user-${NAMES[i % NAMES.length].replace(/\s+/g, '-').toLowerCase()}`,
    author_avatar_url: AUTHOR_AVATARS[row.author_name],
  }));
}

export type DemoHallPost = {
  id: string;
  story: string;
  author_name: string;
  author_initials: string;
  pillar_tag: string;
  witness_count: number;
  is_verified: boolean;
};

export function demoHallOfFamePosts(): DemoHallPost[] {
  return demoCommunityExplorerStories()
    .slice(0, 12)
    .map((s, i) => ({
      id: `hof-${s.id}`,
      story: `${s.title} — ${s.story}`,
      author_name: s.author_name,
      author_initials: s.author_name
        .split(' ')
        .map((p) => p[0])
        .join('')
        .slice(0, 2)
        .toUpperCase(),
      pillar_tag: s.pillar_tag,
      witness_count: s.witness_count,
      is_verified: s.is_verified,
    }));
}

export function shouldServeDemoHallOfFameFallback(
  identifier?: string | null
): boolean {
  return institutionUsesRichDemo(identifier) || individualUsesRichDemo(identifier);
}

export type DemoSocialImport = {
  id: string;
  platform: string;
  content_preview: string;
  thumbnail_url?: string;
  embed_url?: string;
  original_date: string;
  likes_count: number;
  views_count: number;
  pillar_tag?: string;
  added_to_story_hub: boolean;
};

export function demoSocialImports(): DemoSocialImport[] {
  const platforms = [
    'linkedin',
    'twitter',
    'youtube',
    'instagram',
    'tiktok',
    'spotify',
    'blog',
    'linkedin',
    'twitter',
    'youtube',
    'instagram',
    'tiktok',
    'spotify',
    'blog',
    'linkedin',
  ] as const;
  const pillars = [
    'Hero',
    'News',
    'Performance',
    'Achievement',
    'Grit / Mischief',
    'Favorites / Products',
    'History',
    'Performance',
    'Hero',
    'News',
    'Achievement',
    'Favorites / Products',
    'Grit / Mischief',
    'History',
    'Performance',
  ];
  const previews = [
    'Posted a long-form reflection on mutual aid during the ice storm—graphs of volunteer hours plus human moments.',
    'Thread on transit equity: night maps, lighting, and why “feels safe” belongs in city data.',
    'Video walkthrough: digitizing fragile family albums with a phone rig and museum-grade gloves.',
    'Carousel: ten photos from the pop-up market—booth layout, price cards, and rain cover hacks.',
    'Short clip: 45 seconds of tabla practice with on-screen BPM—comments asked for a slower breakdown.',
    'Playlist “Study & Sea” — lo-fi plus field recordings from the ferry; saved by 2.4k listeners.',
    'Blog: “Carry-ons and courage” — essay draft that later became the published piece in the review.',
    'Republished award citation with a paragraph on what verification changed for my parents’ pride.',
    'Live-tweeted hackathon finals—integrity track demos with timestamps for each team’s threat model.',
    'Interview snippet: museum curator on cold storage for audio reels; subtitles in two languages.',
    'Reel: tea ceremony winter setup—angle of light on the chawan, no faces, ASMR-forward.',
    'Podcast clip: youth jazz adjudicator on intonation—“sharp is a habit; listen down.”',
    'Medium post: open-source localization retrospective—what “done” means for volunteer translators.',
    'Newsletter: marathon relay logistics—van assignments, split cards, and gratitude to med tent.',
    'LinkedIn article: clinical rotation—three habits that made preceptors trust my charting.',
  ];
  return platforms.map((platform, i) => ({
    id: `demo-si-${i + 1}`,
    platform,
    content_preview: previews[i],
    thumbnail_url:
      i % 3 === 0
        ? U('photo-1611162617474-5b21e879e113', 400)
        : i % 3 === 1
          ? U('photo-1478737270239-2f02b77fc618', 400)
          : undefined,
    original_date: daysAgo(15 + i * 4),
    likes_count: 12 + i * 3,
    views_count: 180 + i * 42,
    pillar_tag: pillars[i],
    added_to_story_hub: i % 4 === 0,
    ...(platform === 'youtube' && i % 4 === 1
      ? {
          embed_url: 'https://www.youtube-nocookie.com/embed/jNQXAC9IVRw?rel=0&modestbranding=1',
        }
      : {}),
  }));
}

export type DemoStoryPost = {
  id: string;
  content_type: string;
  title: string;
  body: string;
  pillar_tag: string;
  mood_tag: string;
  privacy: string;
  milestone_date: string | null;
  media_url?: string;
  media_alt?: string;
  embed_url?: string;
  external_url?: string;
  is_cover_story: boolean;
  witness_count: number;
  created_at: string;
};

export function demoStoryHubNarrativePosts(): DemoStoryPost[] {
  /** Public embed that stays playable in demo (privacy-enhanced host). */
  const yt = 'https://www.youtube-nocookie.com/embed/jNQXAC9IVRw?rel=0&modestbranding=1';
  return [
    {
      id: 'hub-sp-1',
      content_type: 'written',
      title: 'First winter here: bus transfers, ESL club, one kind teacher',
      body: 'I missed my stop twice the same week. A classmate drew a map on graph paper—arrows, jokes, a tiny sun. I kept it in my wallet until the routes lived in muscle memory.',
      pillar_tag: 'Achievement',
      mood_tag: 'Grateful',
      privacy: 'public',
      milestone_date: '2019-11-08',
      is_cover_story: true,
      witness_count: 47,
      created_at: daysAgo(40),
    },
    {
      id: 'hub-sp-2',
      content_type: 'written',
      title: 'Shovels, tea, frozen walkways',
      body: 'We cleared steps until our shoulders ached, then split a thermos. Someone’s windshield fluid had turned to slush—we laughed like that was a personality trait.',
      pillar_tag: 'Hero',
      mood_tag: 'Hopeful',
      privacy: 'public',
      milestone_date: '2020-12-18',
      is_cover_story: false,
      witness_count: 36,
      created_at: daysAgo(68),
    },
    {
      id: 'hub-sp-3',
      content_type: 'photo',
      title: 'Welcome week: three minutes with a shaking mic',
      body: 'The stage lights looked like honey. I talked about belonging as a practice, not a prize.',
      pillar_tag: 'Performance',
      mood_tag: 'Triumphant',
      privacy: 'public',
      milestone_date: '2021-04-22',
      media_url: U('photo-1503676260728-1c00da094a0b', 900),
      media_alt: 'Auditorium stage warm lights',
      is_cover_story: false,
      witness_count: 52,
      created_at: daysAgo(95),
    },
    {
      id: 'hub-sp-4',
      content_type: 'video',
      title: 'Digitization weekend: gloves, colour cards, patience',
      body: 'Volunteer shift at the museum scan lab—every folio gets a whisper of weight before it touches the glass.',
      pillar_tag: 'History',
      mood_tag: 'Reflective',
      privacy: 'public',
      milestone_date: '2022-02-05',
      embed_url: yt,
      media_url: U('photo-1566127444979-b3d2b14d3f7f', 900),
      media_alt: 'Archive scanning workspace',
      is_cover_story: false,
      witness_count: 29,
      created_at: daysAgo(120),
    },
    {
      id: 'hub-sp-5',
      content_type: 'voice_note',
      title: 'Voice memo: grandmother’s packing list',
      body: 'Audio note—two minutes—tea bricks, photographs with curled corners, a phrase book soft at the edges.',
      pillar_tag: 'History',
      mood_tag: 'Raw',
      privacy: 'private',
      milestone_date: '2015-11-02',
      external_url: 'https://archive.org/details/demo-audio-placeholder',
      is_cover_story: false,
      witness_count: 12,
      created_at: daysAgo(200),
    },
    {
      id: 'hub-sp-6',
      content_type: 'podcast',
      title: 'Podcast diary: episode 04 — “Tuesday the car started”',
      body: 'A short monologue on buying the winter beater, naming it Tuesday, and learning maintenance as self-respect.',
      pillar_tag: 'Grit / Mischief',
      mood_tag: 'Funny',
      privacy: 'public',
      milestone_date: '2023-03-10',
      external_url: 'https://open.spotify.com/show/demo-placeholder',
      is_cover_story: false,
      witness_count: 61,
      created_at: daysAgo(55),
    },
    {
      id: 'hub-sp-7',
      content_type: 'written',
      title: 'Bookstore shelving: minimum wage, maximum poetry',
      body: 'I learned spines face out with kindness—genre, yes, but also height so shorter readers aren’t punished.',
      pillar_tag: 'Performance',
      mood_tag: 'Reflective',
      privacy: 'public',
      milestone_date: '2022-06-01',
      is_cover_story: false,
      witness_count: 33,
      created_at: daysAgo(150),
    },
    {
      id: 'hub-sp-8',
      content_type: 'photo',
      title: 'Night class parking lot: first snow on Tuesday’s hood',
      body: 'Phone photo—grainy, honest. I felt like an adult and a child at once.',
      pillar_tag: 'Favorites / Products',
      mood_tag: 'Hopeful',
      privacy: 'public',
      milestone_date: '2023-01-17',
      media_url: U('photo-1449965408869-eaa3f722e40d', 900),
      media_alt: 'Car hood dusted with snow under streetlight',
      is_cover_story: false,
      witness_count: 41,
      created_at: daysAgo(88),
    },
    {
      id: 'hub-sp-9',
      content_type: 'written',
      title: 'Faith on the hard weeks',
      body: 'Prayer walks before exams; friends who texted verses at 11pm; doubt welcomed at the table.',
      pillar_tag: 'Hero',
      mood_tag: 'Hopeful',
      privacy: 'vault_only',
      milestone_date: '2024-02-14',
      is_cover_story: false,
      witness_count: 19,
      created_at: daysAgo(30),
    },
    {
      id: 'hub-sp-10',
      content_type: 'video',
      title: 'Hackathon demo — sixty seconds on tamper-evident previews',
      body: 'Screen capture walkthrough with voiceover; team stayed quiet until the applause hit.',
      pillar_tag: 'News',
      mood_tag: 'Triumphant',
      privacy: 'public',
      milestone_date: '2025-03-30',
      embed_url: yt,
      media_url: U('photo-1522071820081-009f0129c71c', 900),
      media_alt: 'Team presenting at laptops',
      is_cover_story: false,
      witness_count: 74,
      created_at: daysAgo(12),
    },
    {
      id: 'hub-sp-11',
      content_type: 'written',
      title: 'Community centre finally felt like home',
      body: 'Potluck tables, badminton in the gym, someone remembering my name without me re-introducing.',
      pillar_tag: 'News',
      mood_tag: 'Triumphant',
      privacy: 'public',
      milestone_date: '2025-08-30',
      is_cover_story: false,
      witness_count: 58,
      created_at: daysAgo(6),
    },
    {
      id: 'hub-sp-12',
      content_type: 'photo',
      title: 'Relay finish chute: numb toes, full heart',
      body: 'Mud on calves, medal ribbon tangled—I grinned ugly.',
      pillar_tag: 'Performance',
      mood_tag: 'Triumphant',
      privacy: 'public',
      milestone_date: '2024-10-06',
      media_url: U('photo-1452626038306-9aae5e071dd3', 900),
      media_alt: 'Runners crossing finish area',
      is_cover_story: false,
      witness_count: 63,
      created_at: daysAgo(100),
    },
    {
      id: 'hub-sp-13',
      content_type: 'podcast',
      title: 'Mini-cast: mentors who proofread scary emails',
      body: 'Five-minute episode—shoutouts to instructors who marked up drafts in green ink, not red.',
      pillar_tag: 'Achievement',
      mood_tag: 'Grateful',
      privacy: 'public',
      milestone_date: '2024-04-10',
      external_url: 'https://podcasts.apple.com/podcast/demo-placeholder',
      is_cover_story: false,
      witness_count: 27,
      created_at: daysAgo(70),
    },
    {
      id: 'hub-sp-14',
      content_type: 'voice_note',
      title: 'Whispered practice: tabla bols before the fundraiser',
      body: 'Raw audio—room tone, metronome tick, one deep breath before counting in.',
      pillar_tag: 'Performance',
      mood_tag: 'Raw',
      privacy: 'public',
      milestone_date: '2024-08-02',
      external_url: 'https://archive.org/details/demo-audio-placeholder-2',
      is_cover_story: false,
      witness_count: 22,
      created_at: daysAgo(82),
    },
    {
      id: 'hub-sp-15',
      content_type: 'written',
      title: 'Next chapter: mentorship, civic hours, stories untold',
      body: 'A living list—what I owe the younger cohort, the neighbourhood, and the version of me who was afraid to speak.',
      pillar_tag: 'News',
      mood_tag: 'Hopeful',
      privacy: 'public',
      milestone_date: '2026-01-15',
      is_cover_story: false,
      witness_count: 15,
      created_at: daysAgo(2),
    },
  ];
}

export type DemoMediaVaultItem = {
  id: string;
  media_type: string;
  file_name: string;
  file_url: string;
  file_size_bytes: number | null;
  thumbnail_url?: string;
  caption?: string;
  privacy: string;
  created_at: string;
};

export function demoMediaVaultItems(): DemoMediaVaultItem[] {
  return [
    {
      id: 'mv-1',
      media_type: 'photo',
      file_name: 'welcome_week_stage.jpg',
      file_url: U('photo-1503676260728-1c00da094a0b', 1600),
      file_size_bytes: 890000,
      thumbnail_url: U('photo-1503676260728-1c00da094a0b', 400),
      caption: 'Welcome week speech—house lights half down.',
      privacy: 'public',
      created_at: daysAgo(100),
    },
    {
      id: 'mv-2',
      media_type: 'document',
      file_name: 'esl_portfolio_spring.pdf',
      file_url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
      file_size_bytes: 420000,
      caption: 'Immersion portfolio — instructor signatures on final page.',
      privacy: 'vault_only',
      created_at: daysAgo(200),
    },
    {
      id: 'mv-3',
      media_type: 'audio',
      file_name: 'grandmother_packing_list.m4a',
      file_url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3',
      file_size_bytes: 1200000,
      caption: 'Voice note — tea, photographs, folded phrase book.',
      privacy: 'private',
      created_at: daysAgo(85),
    },
    {
      id: 'mv-4',
      media_type: 'photo',
      file_name: 'tuesday_snow_hood.jpg',
      file_url: U('photo-1449965408869-eaa3f722e40d', 1600),
      file_size_bytes: 760000,
      thumbnail_url: U('photo-1449965408869-eaa3f722e40d', 400),
      caption: 'First snow on the car I could finally drive.',
      privacy: 'public',
      created_at: daysAgo(300),
    },
    {
      id: 'mv-5',
      media_type: 'video',
      file_name: 'scan_lab_broll.mp4',
      file_url: 'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4',
      file_size_bytes: 4_200_000,
      thumbnail_url: U('photo-1566127444979-b3d2b14d3f7f', 400),
      caption: 'Archive digitization — colour card on easel.',
      privacy: 'vault_only',
      created_at: daysAgo(120),
    },
    {
      id: 'mv-6',
      media_type: 'document',
      file_name: 'lifesaving_course_completion.pdf',
      file_url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
      file_size_bytes: 310000,
      caption: 'Bronze medallion — open water module checklist.',
      privacy: 'public',
      created_at: daysAgo(15),
    },
    {
      id: 'mv-7',
      media_type: 'photo',
      file_name: 'zine_table_pop_up.jpg',
      file_url: U('photo-1513475382583-d06e58bcb0e0', 1600),
      file_size_bytes: 640000,
      thumbnail_url: U('photo-1513475382583-d06e58bcb0e0', 400),
      caption: 'Risograph stacks and handwritten price cards.',
      privacy: 'public',
      created_at: daysAgo(175),
    },
    {
      id: 'mv-8',
      media_type: 'audio',
      file_name: 'tabla_practice_take3.wav',
      file_url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3',
      file_size_bytes: 2100000,
      caption: 'Practice log — teentaal at 92 BPM.',
      privacy: 'private',
      created_at: daysAgo(60),
    },
    {
      id: 'mv-9',
      media_type: 'document',
      file_name: 'hackathon_pitch_deck.pdf',
      file_url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
      file_size_bytes: 880000,
      caption: 'Integrity track — threat model appendix.',
      privacy: 'public',
      created_at: daysAgo(10),
    },
    {
      id: 'mv-10',
      media_type: 'photo',
      file_name: 'relay_exchange_zone.jpg',
      file_url: U('photo-1452626038306-9aae5e071dd3', 1600),
      file_size_bytes: 720000,
      thumbnail_url: U('photo-1452626038306-9aae5e071dd3', 400),
      caption: 'Relay handoff — chalk on calves, grin intact.',
      privacy: 'public',
      created_at: daysAgo(130),
    },
    {
      id: 'mv-11',
      media_type: 'video',
      file_name: 'jazz_solo_clip.mp4',
      file_url: 'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4',
      file_size_bytes: 5_100_000,
      thumbnail_url: U('photo-1511192336575-5a79af67a629', 400),
      caption: 'Lead alto — second chorus, eyes closed.',
      privacy: 'vault_only',
      created_at: daysAgo(240),
    },
    {
      id: 'mv-12',
      media_type: 'photo',
      file_name: 'tea_ceremony_setup.jpg',
      file_url: U('photo-1544787219-7f47ccb7651f', 1600),
      file_size_bytes: 690000,
      thumbnail_url: U('photo-1544787219-7f47ccb7651f', 400),
      caption: 'Winter gathering — chawan, mizusashi, low light.',
      privacy: 'private',
      created_at: daysAgo(4),
    },
    {
      id: 'mv-13',
      media_type: 'document',
      file_name: 'published_essay_galleys.pdf',
      file_url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
      file_size_bytes: 540000,
      caption: 'Literary review — author proof pass.',
      privacy: 'public',
      created_at: daysAgo(95),
    },
    {
      id: 'mv-14',
      media_type: 'audio',
      file_name: 'community_centre_ambience.flac',
      file_url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3',
      file_size_bytes: 3_400_000,
      caption: 'Potluck hall — chairs scraping, laughter spike.',
      privacy: 'vault_only',
      created_at: daysAgo(20),
    },
    {
      id: 'mv-15',
      media_type: 'photo',
      file_name: 'clinic_saturday_team.jpg',
      file_url: U('photo-1576091160399-112ba8d25d1d', 1600),
      file_size_bytes: 810000,
      thumbnail_url: U('photo-1576091160399-112ba8d25d1d', 400),
      caption: 'Triage team photo — stickers swapped for crayon stars.',
      privacy: 'private',
      created_at: daysAgo(50),
    },
  ];
}

export type DemoInstitutionPost = {
  id: string;
  story: string;
  pillar_tag: string;
  created_at: string;
  witness_count: number;
  is_verified: boolean;
};

export function demoInstitutionStoryLedgerPosts(): DemoInstitutionPost[] {
  const pillars = [
    'History',
    'Achievement',
    'News',
    'Performance',
    'Hero',
    'History',
    'News',
    'Achievement',
    'Performance',
    'Favorites / Products',
    'Grit / Mischief',
    'History',
    'News',
    'Achievement',
    'Hero',
  ];
  const bodies = [
    'Published our winter listening series schedule—three languages, open captions, and cold-storage audio paths documented end-to-end.',
    'Verified cohort of youth mentors: forty-eight hours logged, two lead instructors countersigned, witness statements attached.',
    'Impact report Q1: 1.2M archive page views, 340 new accession records, volunteer retention up 9% quarter on quarter.',
    'Behind-the-scenes: rotating a fragile garment mannequin without stressing silk thread—conservation camera log included.',
    'Campaign story: “Neighbours Read Together” — little libraries refilled, 14 block captains onboarded, weather SMS templates localized.',
    'Heritage note: shipwright marginalia matched to 1924 weather ledgers; open data drop scheduled for next month.',
    'Trust & safety office hours: walked institutions through credential preview modes and abuse reporting SLAs.',
    'Student archive wall refreshed with composite portraits—consent forms v3.2 on every file.',
    'Lighthouse lens cleaning livestream raised sensor funds; thank-you reel pinned to our public vault.',
    'Member spotlight: lead alto shares practice room rituals—metronome diplomacy and section empathy.',
    'Youth story lab pilot: teen podcast residency applications open; stipends and mentor pairings announced.',
    'Oral history training deck updated—new modules on trauma-informed pacing and interpreter handoffs.',
    'Maritime mini-exhibit: rope tension diagrams beside original cargo tallies; QR links to full finding aids.',
    'Annual volunteer recognition: gold citations for winter response, silver for digitization marathon.',
    'Mission update: accessibility audit complete; caption coverage now 98% on public video holds.',
  ];
  return bodies.map((story, i) => ({
    id: `inst-post-${i + 1}`,
    story,
    pillar_tag: pillars[i],
    created_at: daysAgo(i * 2),
    witness_count: 18 + i * 4,
    is_verified: true,
  }));
}

export function demoInstitutionHallRecords(institutionUserId: string): any[] {
  const achievers = [
    { name: 'Sophie Lin', vault: 'demo-vault-individual' },
    { name: 'Rowan Okonkwo', vault: 'vault-rowan-o' },
    { name: 'Sienna Park', vault: 'vault-sienna-p' },
    { name: 'Mateo Reyes', vault: 'vault-mateo-r' },
    { name: 'Amara Singh', vault: 'vault-amara-s' },
    { name: 'Leo Nakamura', vault: 'vault-leo-n' },
    { name: 'Jordan Malik', vault: 'vault-jordan-m' },
    { name: 'Elena Vasquez', vault: 'vault-elena-v' },
    { name: 'Chris Oduya', vault: 'vault-chris-o' },
    { name: 'Priya Natarajan', vault: 'vault-priya-n' },
    { name: 'Noah Bertrand', vault: 'vault-noah-b' },
    { name: 'Fatima Al-Hassan', vault: 'vault-fatima-a' },
    { name: 'Theo Lindberg', vault: 'vault-theo-l' },
    { name: 'Grace Okafor', vault: 'vault-grace-o' },
    { name: 'Ben Carter', vault: 'vault-ben-c' },
  ];
  const ach = demoAchievementsStoryHubShape();
  return ach.map((a, i) => ({
    id: `inst-hof-${a.id}`,
    title: a.title,
    pillar: a.pillar,
    status: 'verified',
    submitted_date: a.submitted_date,
    achievement_date: a.achievement_date,
    description: a.description,
    institution: institutionUserId,
    verifications: [
      {
        verification_ref: a.verification_ref,
        verified_at: a.verified_at,
        verified_by: a.verified_by,
        verification_type: 'gold',
      },
    ],
    user_profiles: {
      id: `profile-${i}`,
      full_name: achievers[i % achievers.length].name,
      vault_id: achievers[i % achievers.length].vault,
      avatar_url: i % 4 === 0 ? U('photo-1494790108377-be9c29b29330', 200) : null,
    },
  }));
}

export function demoInstitutionDirectory(): any[] {
  const types = ['university', 'government', 'organization'] as const;
  return [
    'Harbour City Archives',
    'Straitview University',
    'Pacific Maritime Trust',
    'Coastal Health Archives',
    'Riverbend Film Guild',
    'North Shore Library Federation',
    'InnovateBC',
    'Youth Jazz Alliance',
    'Open Heritage Localization',
    'Metro Civic Awards',
    'Lighthouse Preservation Guild',
    'Pacific Tea Culture Institute',
    'Strait Robotics League',
    'Women in Shipbuilding Oral History',
    'Coastal Marathon Charities',
  ].map((name, i) => ({
    id: `inst-dir-${i + 1}`,
    full_name: name,
    institution_name: name,
    institution_id: `ext-inst-${i + 1}`,
    institution_type: types[i % types.length],
    created_at: new Date(2020 + (i % 5), (i % 12) + 1, 8).toISOString(),
  }));
}

/** Achievements scoped to institution user id (column `institution` in DB). */
export function demoInstitutionAchievementPipeline(institutionUserId: string): any[] {
  return demoAchievementsStoryHubShape().map((a, i) => ({
    id: a.id,
    achievement_ref: a.achievement_ref,
    title: a.title,
    pillar: a.pillar,
    description: a.description,
    status: a.status,
    user_id: `demo-submitter-${String(i + 1).padStart(2, '0')}`,
    submitted_date: a.submitted_date,
    institution: institutionUserId,
  }));
}

export function demoInstitutionVerificationsForPipeline(
  institutionUserId: string,
  achievements: Array<{
    id: string;
    title: string;
    status: string;
    verification_ref?: string;
    verified_at?: string;
  }>
): any[] {
  return achievements
    .filter((a) => a.status === 'verified')
    .map((a, i) => ({
      id: `demo-vrf-${a.id}`,
      verification_ref: a.verification_ref || `VRF-DEMO-${i + 1}`,
      verified_at: a.verified_at || daysAgo(3 + i),
      achievement_id: a.id,
      institution_id: institutionUserId,
      achievements: { title: a.title },
    }));
}

export function demoInstitutionRecordsMixed(institutionUserId: string): any[] {
  const rows = [
    ['Founding charter — digitized vellum scan', 'History', '1847-03-12', true],
    ['Elders’ circle audio reel #12 (conserved)', 'History', '1988-06-01', true],
    ['Newcomer stories accession batch A-440', 'Achievement', '2016-05-14', true],
    ['Lighthouse lens conservation report', 'Performance', '2023-08-19', true],
    ['Youth podcast residency charter', 'News', '2025-07-15', true],
    ['Shipwright marginalia finding aid', 'History', '2024-01-30', true],
    ['Annual gala verification program', 'Achievement', '2025-09-20', true],
    ['Volunteer citation template pack', 'Hero', '2019-11-09', false],
    ['Exhibit deinstall checklist v2', 'Performance', '2021-03-22', false],
    ['Education dept. risk assessment', 'News', '2022-10-01', false],
    ['Digitization weekend volunteer roster', 'Achievement', '2024-02-10', false],
    ['Member spotlight release forms', 'Favorites / Products', '2024-04-28', false],
  ];
  return rows.map(([title, pillar, date, heritage], i) => ({
    id: `demo-ir-${institutionUserId.slice(0, 8)}-${i + 1}`,
    record_title: title,
    pillar,
    date_of_record: date,
    historical_context: 'Preserved for public heritage search and classroom visits.',
    is_heritage: heritage,
    created_at: daysAgo(400 - i * 28),
    institution_id: institutionUserId,
    archived_by: DEMO_INSTITUTION_DISPLAY_NAME,
  }));
}

export function demoInstitutionStoryPostsRaw(institutionUserId: string): any[] {
  return demoInstitutionStoryLedgerPosts().map((p) => ({
    id: p.id,
    user_id: institutionUserId,
    is_institution: true,
    pillar_tag: p.pillar_tag,
    created_at: p.created_at,
    story: p.story,
  }));
}

const SUBMITTER_NAMES = [
  'Sophie Lin',
  'Rowan Okonkwo',
  'Sienna Park',
  'Mateo Reyes',
  'Amara Singh',
  'Leo Nakamura',
  'Jordan Malik',
  'Elena Vasquez',
];

export function demoInstitutionPendingQueueItems(): any[] {
  const ach = demoAchievementsStoryHubShape().filter((a) =>
    ['pending', 'under_review'].includes(a.status)
  );
  const now = Date.now();
  return ach.map((a, i) => {
    const submitted = new Date(a.submitted_date);
    const daysAgoN = Math.max(1, Math.floor((now - submitted.getTime()) / 86400000));
    let urgency: 'high' | 'medium' | 'low' = 'low';
    if (daysAgoN >= 10) urgency = 'high';
    else if (daysAgoN >= 5) urgency = 'medium';
    return {
      id: a.id,
      achievement_ref: a.achievement_ref,
      title: a.title,
      individualName: SUBMITTER_NAMES[i % SUBMITTER_NAMES.length],
      individualId: `vault-${(i % SUBMITTER_NAMES.length) + 1}`,
      user_id: `demo-submitter-${i + 1}`,
      pillar: a.pillar,
      submittedDate: submitted.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
      daysAgo: daysAgoN,
      description: a.description,
      notes: '',
      proofFile: '',
      urgency,
    };
  });
}

export function demoInstitutionRecentActivityItems(institutionUserId: string): {
  items: Array<{ id: string; type: 'verified' | 'archived'; title: string; time: string }>;
  stats: { totalArchived: number; verificationsCompleted: number; membersOnLedger: number };
} {
  const v = demoAchievementsStoryHubShape()
    .filter((a) => a.status === 'verified')
    .slice(0, 5);
  const r = demoInstitutionRecordsMixed(institutionUserId).slice(0, 4);
  const verifItems = v.map((a, i) => ({
    id: `act-v-${a.id}`,
    type: 'verified' as const,
    title: a.title,
    time: new Date(a.verified_at || daysAgo(i)).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
  }));
  const archItems = r.map((rec: any) => ({
    id: `act-r-${rec.id}`,
    type: 'archived' as const,
    title: rec.record_title,
    time: new Date(rec.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  }));
  const combined = [...verifItems, ...archItems]
    .sort((a, b) => b.time.localeCompare(a.time))
    .slice(0, 8);
  return {
    items: combined,
    stats: {
      totalArchived: 47,
      verificationsCompleted: 218,
      membersOnLedger: 63,
    },
  };
}

export function demoInstitutionVerificationTimelineEntries(): any[] {
  const verified = demoAchievementsStoryHubShape().filter((a) => a.status === 'verified');
  return verified.map((a, i) => ({
    id: `tl-${a.id}`,
    verificationRef: a.verification_ref || `VRF-${i + 1}`,
    achievementTitle: a.title,
    pillar: a.pillar,
    individualName: SUBMITTER_NAMES[i % SUBMITTER_NAMES.length],
    reviewer: `${DEMO_INSTITUTION_DISPLAY_NAME} · Records desk ${(i % 3) + 1}`,
    verifiedAt: a.verified_at || daysAgo(2 + i),
    decisionReason: 'Meets published verification standard; witness bundle complete.',
    verificationType: i % 4 === 0 ? 'heritage' : 'gold',
    isHeritage: i % 5 === 0,
  }));
}

export function demoHeritageRecordsCards(institutionUserId: string): any[] {
  return demoInstitutionRecordsMixed(institutionUserId).filter((r: any) => r.is_heritage);
}

export function demoIndividualPendingSubmissions(): any[] {
  return demoAchievementsStoryHubShape()
    .filter((a) => ['pending', 'under_review'].includes(a.status))
    .map((a, i) => ({
      id: a.id,
      achievement_ref: a.achievement_ref,
      title: a.title,
      pillar: a.pillar,
      institution: VERIFYERS[i % VERIFYERS.length],
      submitted_date: a.submitted_date,
      status: a.status,
      description: a.description,
    }));
}

export function buildDemoAchievementMediaByAchievementIds(ids: string[]): Record<
  string,
  Array<{
    id: string;
    media_type: string;
    file_url: string;
    file_name: string;
    caption: string;
    verification_method: string;
  }>
> {
  const out: Record<string, any[]> = {};
  const samples = [
    {
      media_type: 'photo',
      file_url: U('photo-1523580846011-d3a5bc25702b', 800),
      file_name: 'ceremony.jpg',
      caption: 'Verification ceremony — cohort pin presentation.',
      verification_method: 'institution_review',
    },
    {
      media_type: 'document',
      file_url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
      file_name: 'signed_record.pdf',
      caption: 'Countersigned achievement record.',
      verification_method: 'document_scan',
    },
    {
      media_type: 'photo',
      file_url: U('photo-1500530855697-b586d89ba3ee', 800),
      file_name: 'witness_group.jpg',
      caption: 'Witness pool — instructor and peer leads.',
      verification_method: 'witness_bundle',
    },
  ];
  ids.forEach((id, idx) => {
    const n = idx % 3;
    out[id] = samples.slice(0, n + 1).map((s, j) => ({
      id: `am-${id}-${j}`,
      ...s,
    }));
  });
  return out;
}

// ---------------------------------------------------------------------------
// Credential Trust Records demo data
// ---------------------------------------------------------------------------

export type DemoCredentialTrustRecord = {
  id: string;
  user_id: string;
  title: string;
  issuer_name: string;
  recipient_name: string;
  issue_date: string;
  credential_type: string;
  description: string;
  document_url: string | null;
  thumbnail_url: string | null;
  pillar: string;
  visibility: 'private' | 'shared' | 'public';
  trust_level:
    | 'self_documented'
    | 'media_supported'
    | 'community_confirmed'
    | 'institution_recognized'
    | 'ledger_verified';
  review_status: 'draft' | 'pending' | 'under_review' | 'approved' | 'rejected' | 'needs_info';
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  verification_ref: string | null;
  tags: string[];
  created_at: string;
};

export function demoCredentialTrustRecords(userId: string): DemoCredentialTrustRecord[] {
  const name = DEMO_INDIVIDUAL_DISPLAY_NAME;
  return [
    {
      id: `ctr-demo-001`,
      user_id: userId,
      title: 'Bachelor of Arts — Community Studies',
      issuer_name: 'Coastal Pacific University',
      recipient_name: name,
      issue_date: '2019-06-15',
      credential_type: 'diploma',
      description:
        'Four-year degree in Community Studies with a focus on social impact and cultural preservation.',
      document_url: null,
      thumbnail_url: null,
      pillar: 'Achievement',
      visibility: 'public',
      trust_level: 'institution_recognized',
      review_status: 'approved',
      reviewed_by: 'Coastal Pacific University Registry',
      reviewed_at: daysAgo(180),
      review_notes: null,
      verification_ref: 'VER-CTR-CPF-2019-BA',
      tags: ['education', 'degree', 'community'],
      created_at: daysAgo(180),
    },
    {
      id: `ctr-demo-002`,
      user_id: userId,
      title: 'Certified Oral History Practitioner',
      issuer_name: 'North Shore Oral History Society',
      recipient_name: name,
      issue_date: '2022-03-20',
      credential_type: 'certificate',
      description:
        'Professional certification in oral history methodology, community interview ethics, and archival standards.',
      document_url: null,
      thumbnail_url: null,
      pillar: 'History',
      visibility: 'public',
      trust_level: 'ledger_verified',
      review_status: 'approved',
      reviewed_by: 'NSOHS Certification Board',
      reviewed_at: daysAgo(60),
      review_notes: null,
      verification_ref: 'VER-CTR-NSOHS-2022-OHP',
      tags: ['oral history', 'certification', 'heritage'],
      created_at: daysAgo(60),
    },
    {
      id: `ctr-demo-003`,
      user_id: userId,
      title: 'Community Leadership Award',
      issuer_name: 'Metro Civic Awards Council',
      recipient_name: name,
      issue_date: '2023-11-10',
      credential_type: 'award',
      description:
        'Recognition for outstanding contribution to newcomer integration programs and cross-cultural community building.',
      document_url: null,
      thumbnail_url: null,
      pillar: 'Hero',
      visibility: 'public',
      trust_level: 'community_confirmed',
      review_status: 'approved',
      reviewed_by: null,
      reviewed_at: daysAgo(30),
      review_notes: null,
      verification_ref: 'VER-CTR-MCAC-2023-CLA',
      tags: ['leadership', 'award', 'community'],
      created_at: daysAgo(30),
    },
    {
      id: `ctr-demo-004`,
      user_id: userId,
      title: 'Volunteer Service Record — 500 Hours',
      issuer_name: 'Riverbend Heritage Foundation',
      recipient_name: name,
      issue_date: '2024-08-01',
      credential_type: 'volunteer',
      description:
        'Completed 500 hours of volunteer service in cultural heritage archiving, oral history interviews, and community event coordination.',
      document_url: null,
      thumbnail_url: null,
      pillar: 'Grit / Mischief',
      visibility: 'shared',
      trust_level: 'media_supported',
      review_status: 'pending',
      reviewed_by: null,
      reviewed_at: null,
      review_notes: null,
      verification_ref: null,
      tags: ['volunteer', 'service', 'heritage'],
      created_at: daysAgo(10),
    },
    {
      id: `ctr-demo-005`,
      user_id: userId,
      title: 'Digital Preservation Training Completion',
      issuer_name: 'Pacific Heritage Museum',
      recipient_name: name,
      issue_date: '2024-12-01',
      credential_type: 'training',
      description:
        'Completed intensive 40-hour training in digital asset preservation, metadata standards, and long-term archival techniques.',
      document_url: null,
      thumbnail_url: null,
      pillar: 'Performance',
      visibility: 'private',
      trust_level: 'self_documented',
      review_status: 'draft',
      reviewed_by: null,
      reviewed_at: null,
      review_notes: null,
      verification_ref: null,
      tags: ['training', 'digital preservation'],
      created_at: daysAgo(3),
    },
  ];
}

// ---------------------------------------------------------------------------
// Heritage Collections + Archive Items demo data
// ---------------------------------------------------------------------------

export type DemoHeritageCollection = {
  id: string;
  owner_id: string;
  owner_type: 'individual' | 'institution';
  title: string;
  description: string;
  cover_image_url: string | null;
  visibility:
    | 'private'
    | 'shared'
    | 'community_only'
    | 'institution_only'
    | 'public'
    | 'restricted_cultural';
  collection_type: 'personal' | 'family' | 'community' | 'cultural' | 'institutional';
  item_count: number;
  created_at: string;
};

export type DemoHeritageArchiveItem = {
  id: string;
  collection_id: string;
  owner_id: string;
  title: string;
  description: string;
  item_type:
    | 'photo'
    | 'video'
    | 'audio'
    | 'document'
    | 'certificate'
    | 'oral_history'
    | 'community_story'
    | 'cultural_record'
    | 'scanned_record';
  file_url: string | null;
  thumbnail_url: string | null;
  file_type: string | null;
  file_size_bytes: number | null;
  file_hash: string | null;
  storage_status: 'stored' | 'archived' | 'pending' | 'failed';
  contributor: string;
  source: string;
  origin_description: string;
  rights_status:
    | 'all_rights_reserved'
    | 'creative_commons'
    | 'public_domain'
    | 'restricted'
    | 'pending_review';
  consent_status: 'obtained' | 'not_required' | 'pending' | 'denied';
  cultural_context: string;
  review_status: 'draft' | 'published' | 'under_review' | 'archived';
  visibility:
    | 'private'
    | 'shared'
    | 'community_only'
    | 'institution_only'
    | 'public'
    | 'restricted_cultural';
  pillar: string;
  created_at: string;
};

export function demoHeritageCollections(
  ownerId: string,
  ownerType: 'individual' | 'institution' = 'individual'
): DemoHeritageCollection[] {
  return [
    {
      id: `hc-demo-001`,
      owner_id: ownerId,
      owner_type: ownerType,
      title: 'Family Migration Story — Andrade Archive',
      description:
        'Three generations of migration records, photographs, and oral histories tracing the Andrade family journey from the Azores to the Pacific Northwest.',
      cover_image_url: U('photo-1511895426328-dc8714191011'),
      visibility: 'community_only',
      collection_type: 'family',
      item_count: 14,
      created_at: daysAgo(120),
    },
    {
      id: `hc-demo-002`,
      owner_id: ownerId,
      owner_type: ownerType,
      title: 'Elder Wisdom — Voice Recording Series',
      description:
        'Oral history recordings with seven community elders, preserving knowledge about traditional crafts, seasonal ceremonies, and community governance.',
      cover_image_url: U('photo-1508739773434-c26b3d09e071'),
      visibility: 'restricted_cultural',
      collection_type: 'cultural',
      item_count: 9,
      created_at: daysAgo(75),
    },
    {
      id: `hc-demo-003`,
      owner_id: ownerId,
      owner_type: ownerType,
      title: 'Newcomer Journey 2018 — Personal Milestones',
      description:
        'A personal archive of arrival documents, first-home photos, community welcome events, and milestone certificates from the first year after resettlement.',
      cover_image_url: U('photo-1529156069898-49953e39b3ac'),
      visibility: 'private',
      collection_type: 'personal',
      item_count: 22,
      created_at: daysAgo(40),
    },
  ];
}

export function demoHeritageArchiveItems(
  collectionId: string,
  ownerId: string
): DemoHeritageArchiveItem[] {
  return [
    {
      id: `hai-demo-001`,
      collection_id: collectionId,
      owner_id: ownerId,
      title: 'Arrival photograph — Vancouver port, 1971',
      description:
        'Black-and-white photograph of Manuel and Rosa Andrade with three children at the port of Vancouver, taken by a harbor official on the day of arrival.',
      item_type: 'photo',
      file_url: null,
      thumbnail_url: U('photo-1516483638261-f4dbaf036963', 400),
      file_type: 'image/jpeg',
      file_size_bytes: 4200000,
      file_hash: 'sha256:a1b2c3d4e5f6...',
      storage_status: 'stored',
      contributor: 'Andrade Family Estate',
      source: 'Private family collection',
      origin_description: 'Original print digitized by Pacific Heritage Museum, 2023',
      rights_status: 'all_rights_reserved',
      consent_status: 'obtained',
      cultural_context:
        'Documents Azorean emigration wave to the Pacific Northwest during the 1970s.',
      review_status: 'published',
      visibility: 'community_only',
      pillar: 'History',
      created_at: daysAgo(115),
    },
    {
      id: `hai-demo-002`,
      collection_id: collectionId,
      owner_id: ownerId,
      title: 'Elder Maria Coelho — Harvest ceremony oral account',
      description:
        'Recorded interview with Elder Maria Coelho, age 84, describing the autumn harvest ceremony practiced in her village for over 200 years.',
      item_type: 'oral_history',
      file_url: null,
      thumbnail_url: null,
      file_type: 'audio/mp3',
      file_size_bytes: 38000000,
      file_hash: 'sha256:b9c8d7e6f5a4...',
      storage_status: 'archived',
      contributor: 'North Shore Oral History Society',
      source: 'NSOHS Community Archive Project 2022',
      origin_description: "Recorded in Elder Coelho's home, witnessed by two family members",
      rights_status: 'restricted',
      consent_status: 'obtained',
      cultural_context:
        'Part of the Living Traditions documentation project, preserving endangered cultural knowledge.',
      review_status: 'published',
      visibility: 'restricted_cultural',
      pillar: 'History',
      created_at: daysAgo(70),
    },
    {
      id: `hai-demo-003`,
      collection_id: collectionId,
      owner_id: ownerId,
      title: 'Permanent Residency Certificate — 2018',
      description:
        'Scanned permanent residency certificate issued upon completion of resettlement process. Original document held in family archive.',
      item_type: 'scanned_record',
      file_url: null,
      thumbnail_url: null,
      file_type: 'application/pdf',
      file_size_bytes: 1200000,
      file_hash: 'sha256:c3d4e5f6a7b8...',
      storage_status: 'stored',
      contributor: DEMO_INDIVIDUAL_DISPLAY_NAME,
      source: 'Personal documents',
      origin_description: 'Government-issued document, digitized for ledger preservation',
      rights_status: 'all_rights_reserved',
      consent_status: 'not_required',
      cultural_context: 'Part of newcomer journey milestone documentation.',
      review_status: 'published',
      visibility: 'private',
      pillar: 'Achievement',
      created_at: daysAgo(38),
    },
  ];
}

// Institution demo credential review queue
export type DemoCredentialReviewItem = {
  id: string;
  user_id: string;
  user_name: string;
  user_handle: string;
  title: string;
  issuer_name: string;
  credential_type: string;
  trust_level: string;
  review_status: 'pending' | 'under_review' | 'approved' | 'rejected' | 'needs_info';
  submitted_at: string;
  description: string;
  has_document: boolean;
};

export function demoInstitutionCredentialReviewQueue(): DemoCredentialReviewItem[] {
  return [
    {
      id: `icr-demo-001`,
      user_id: `uid-rev-001`,
      user_name: 'Lena Vasquez',
      user_handle: 'lena.v',
      title: 'Advanced Community Mediation Certificate',
      issuer_name: 'Riverbend Heritage Foundation',
      credential_type: 'certificate',
      trust_level: 'media_supported',
      review_status: 'pending',
      submitted_at: daysAgo(2),
      description: 'Completion of 80-hour advanced mediation course with case study portfolio.',
      has_document: true,
    },
    {
      id: `icr-demo-002`,
      user_id: `uid-rev-002`,
      user_name: 'James Okafor',
      user_handle: 'james.ok',
      title: 'Youth Leadership Program Diploma',
      issuer_name: 'Metro Civic Awards Council',
      credential_type: 'diploma',
      trust_level: 'community_confirmed',
      review_status: 'under_review',
      submitted_at: daysAgo(5),
      description:
        'Two-year youth leadership program with mentorship component and community project delivery.',
      has_document: true,
    },
    {
      id: `icr-demo-003`,
      user_id: `uid-rev-003`,
      user_name: 'Priya Sharma',
      user_handle: 'priya.sh',
      title: 'Cultural Heritage Volunteer Recognition',
      issuer_name: 'Pacific Heritage Museum',
      credential_type: 'recognition',
      trust_level: 'self_documented',
      review_status: 'needs_info',
      submitted_at: daysAgo(8),
      description:
        "Recognition for 200+ hours of volunteer contribution to the museum's digitization project.",
      has_document: false,
    },
  ];
}
